import { ContextProvider } from "@lit/context";
import { css, html, TemplateResult } from "lit";
import { unsafeHTML } from "lit-html/directives/unsafe-html.js";
import { customElement, property } from "lit/decorators.js";

// Lit'in type:Boolean'ı attribute varlığına bakar; "false" stringini tanımaz.
// Bu converter HTML'den show-price="false" gibi değerleri doğru çevirir.
const boolAttr = {
  fromAttribute: (v: string | null) => v !== null && v !== "false" && v !== "0",
  toAttribute: (v: boolean) => String(v),
};
import { Api } from "../../api/Api.js";
import { SugarProductElement } from "../../core/product/SugarProductElement";
import { SCENE_EDITOR_CAPABILITIES } from "../../core/product/viewportCapabilities";
import { CONFIGURATOR_ITEM_KIND } from "./ConfiguratorPendingDrag";
import {
  configuratorContext,
  SugarConfiguratorContext,
} from "./SugarConfiguratorContext.js";
import("./SugarConfiguratorSide");
import("./SugarConfiguratorOperations");
import("./SugarProforma.js");
import("../../core/ui-kit/FloatMenu");
import("../../core/viewport/SugarCanvas");
import("./ConfiguratorCapabilityElements");
import("../room-designer/ui/DesignerTransformerElement");
import("../../core/input/PointerGestureInterpreterElement");
// Lazy — keeps the configurator's first-load behaviour unchanged.
import("../../core/viewport/registerPostProcessElements");
import { configuratorIcons } from "../../icons/ConfiguratorIcons.js";
import sugarConfiguratorCss from "./SugarConfiguratorCSS";
import "../../debug/scene-debug/SceneDebugMenu";

const style = css`
  :host {
    display: block;
    height: 100vh;
    box-sizing: border-box;
  }

  .container {
    display: flex;
    flex-direction: row;
    height: 100%;
    position: relative;
    overflow: hidden;
  }

  aside {
    width: 360px;
    border-right: 1px solid #ddd;
    padding: 2px;
    overflow-y: auto;
    background-color: #fafafa;
    height: 100%;
  }

  left-side {
    width: 360px;
    height: 100%;
    border-right: 1px solid #ddd;
    padding: 2px;
    overflow: scroll;
    display: flex;
    flex-direction: column;
  }

  up-side {
    position: absolute;
    top: 0px;
    left: 50%;
    z-index: 9999;
  }

  main {
    flex: 1;
    padding: 2px;
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
    position: relative;
  }

  .viewer-container {
    flex: 1;
    width: 100%;
    display: flex;
    align-items: stretch;
    justify-content: stretch;
    overflow: hidden;
  }

  .viewer-container sugar-viewport {
    width: 100%;
    height: 100%;
    display: block;
  }

  .placeholder {
    background: #eee;
    border: 2px dashed #ccc;
    width: 100%;
    max-width: 600px;
    height: 400px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    color: #888;
  }

  @media (max-width: 768px) {
    .container {
      flex-direction: column;
    }

    aside {
      width: 100%;
      border-right: none;
      border-bottom: 1px solid #ddd;
    }

    .viewer-container {
      padding: 16px;
    }
  }
`;

@customElement("sugar-configurator")
export class SugarConfigurator extends SugarProductElement {
  // Viewport lives in light DOM under this shadow; keep the old content-host
  // rules (`.center`, `.css-renderer`, …) so canvas sizing/overlays still work.
  // Content CSS first; facade layout rules win on shared selectors like `.container`.
  static styles = [sugarConfiguratorCss, style];
  protected override useShadowRoot = true;
  protected override renderSettingsScope = "configurator" as const;

  private _configuratorId?: string;
  private _configuratorContextProvider;

  constructor() {
    super();
    this._configuratorContextProvider = new ContextProvider(this, {
      context: configuratorContext,
      initialValue: new SugarConfiguratorContext(this),
    });
  }

  @property({ attribute: "show-dimensions", converter: boolAttr })
  showDimensions = true;

  @property({ attribute: "show-clear", converter: boolAttr })
  showClear = true;

  @property({ attribute: "show-top-view", converter: boolAttr })
  showTopView = true;

  @property({ attribute: "show-product-list", converter: boolAttr })
  showProductList = true;

  @property({ attribute: "show-price", converter: boolAttr })
  showPrice = true;

  @property({ attribute: "show-create-render", converter: boolAttr })
  showCreateRender = false;

  @property({ attribute: "configurator-id", type: String })
  public set configuratorId(value: string) {
    this._configuratorId = value;
    this._configuratorContextProvider.value.configuratorId = value;

    Api.fetchConfigurator(value).then((data) => {
      this._configuratorContextProvider.value.model = data;
    });
  }

  public get configuratorId(): string {
    return this._configuratorId!;
  }

  // Viewport bileşimi + pipeline bağlantısı SugarProductElement'te.
  protected override capabilities = SCENE_EDITOR_CAPABILITIES;

  protected override capabilityProps() {
    return { "sugar-item-drop": { accept: CONFIGURATOR_ITEM_KIND } };
  }

  protected override viewportOptions = {
    noEnvironment: true,
    noDefaultCamera: true,
  };

  override disconnectedCallback(): void {
    this._configuratorContextProvider.value.dispose();
    super.disconnectedCallback();
  }

  protected renderHtml(): TemplateResult {
    return html /* html */ `
      <div class="container">
        <main>
          <div class="viewer-container">
            ${this.renderViewport(html`
              <configurator-session mode="configurator"></configurator-session>
              <configurator-drag-preview></configurator-drag-preview>
            `)}
          </div>

          <slot name="operations">
            <sugar-configurator-operations
              .showDimensions=${this.showDimensions}
              .showClear=${this.showClear}
              .showTopView=${this.showTopView}
              .showProductList=${this.showProductList}
              .showPrice=${this.showPrice}
              .showCreateRender=${this.showCreateRender}
            ></sugar-configurator-operations>
          </slot>
        </main>

        <aside>
          <sugar-configurator-side></sugar-configurator-side>
        </aside>
      </div>
      ${unsafeHTML(configuratorIcons)}
    `;
  }
}
